import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { logoutRedirectTarget } from '../lib/logoutRedirect.js';

// /logout?next=<...> — partner report 2026-09-09: the ChatGPT connector's
// consent page (dathent-mcp-worker renderConsentPage) had no way to sign out
// and connect a different Dathent account. Its "Not you? Use a different
// account" link now points here with ?next= carrying the pending authorize
// URL. This route does the same sign-out AuthContext.logout() already does
// for the Sidebar's own sign-out button (Sidebar.jsx handleSignOut — calls
// logout(), which clears the user + the API client's cache, i.e. every
// per-user local bit that button clears), then hands the browser to /login,
// carrying ?next= through (re-validated by logoutRedirectTarget/safeNext)
// so signing back in as someone else resumes the pending ChatGPT
// authorization instead of landing on a bare dashboard. Renders nothing —
// it is a pure redirect step, gone the instant it does its job.
export default function Logout() {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const next = new URLSearchParams(location.search).get('next');
    const target = logoutRedirectTarget(next);
    (async () => {
      try {
        await logout();
      } finally {
        navigate(target, { replace: true });
      }
    })();
    // Deliberately run once on mount only — location.search / logout /
    // navigate are read into the effect's own captured `next`/`target` at
    // that instant, and re-running on their identity churn (navigate is a
    // stable ref from react-router, but logout comes off context) would risk
    // firing the sign-out call twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
